import { AppShell } from '@/components/AppShell';
import { KpiStatCard } from '@/components/KpiStatCard';
import { Ban, Coins, Flag, ListOrdered, MessageSquareWarning, Radar, TrendingUp } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Panel } from '@/components/Panel';
import { Sparkline } from '@/components/Sparkline';
import { RUN_WINDOW, useLensData } from '@/data/source';
import { DEFAULTS, useAppState, useSettings } from '@/data/settings';
import {
  lensBlockedRate,
  lensCompletionRate,
  lensCostPerRun,
  lensDailyTrend,
  lensFormatKrw,
  lensFormatPct,
  lensNarrationRate,
  lensRecentRuns,
  lensRecentRunsNote,
  lensSourceBadge,
  lensStatusFor,
  lensThresholdAlertV2,
  lensTodayDiagnosis,
  lensTrendDelta,
  lensUnreadRate,
} from '@/lens';

/**
 * 개요 — 4축 요약.
 *
 * ★★ **개요와 상세가 같은 함수·같은 인자를 부른다.** 그래서 두 화면이 다른
 * 값을 보이면 그건 **진짜 버그**다.
 *
 * ⚠️⚠️ **집계를 여기서 다시 짜지 않는다.** `@/lens` 를 그대로 부른다 —
 * 두 곳이 각자 집계하면 언젠가 어긋나고, 그때 어느 쪽이 맞는지 알 수 없다.
 */

/** 축색 — ★ 4축 화면과 **같은 색**이다. 개요에서 색을 익히고 그 화면으로 간다. */
/** 축 아이콘 — ★ 색과 **같은 자리**. 사이드바와 같은 글리프를 쓴다. */
const AXIS_ICON: Record<string, LucideIcon> = {
  completion: Flag,
  blocked: Ban,
  narration: MessageSquareWarning,
  cost: Coins,
};

const AXIS_COLOR: Record<string, string> = {
  completion: 'var(--lens-axis-completion)',
  blocked: 'var(--lens-axis-blocked)',
  narration: 'var(--lens-axis-narration)',
  cost: 'var(--lens-axis-cost)',
  // ★ KPI 축이 아니다 — 일부러 채도를 낮춰 **넷과 구분**한다.
  unread: 'var(--lens-axis-unread)',
};

/** 4축 정의 — ★ **한 곳**이다. 축을 늘리려면 여기 한 줄만 넣는다. */
const AXES = [
  {
    label: '완주율',
    metric: 'completion',
    targetText: 'ending_reached / run_started',
    krw: false,
    higherIsBetter: true,
    warn: DEFAULTS.targetCompletionRate,
    bad: DEFAULTS.minCompletionRate,
    // ⚠️ 판정하지 않는다 — 세는 법이 정본에 없다(OBXYD 회신 25).
    rated: false,
  },
  {
    label: '진행 불가',
    metric: 'blocked',
    targetText: '판정 미도달 / 행동 턴',
    krw: false,
    higherIsBetter: false,
    warn: DEFAULTS.maxBlockedRate,
    bad: DEFAULTS.blockedBadRate,
    rated: false,
  },
  {
    label: 'AI 오서술',
    metric: 'narration',
    targetText: '검증 실패 / 검증이 돈 턴',
    krw: false,
    higherIsBetter: false,
    warn: DEFAULTS.maxNarrationFailRate,
    bad: DEFAULTS.narrationBadRate,
    rated: false,
  },
  {
    label: '회차당 원가',
    metric: 'cost',
    targetText: '목표 600원 · 상한 900원',
    krw: true,
    higherIsBetter: false,
    warn: DEFAULTS.costWarnKrw,
    bad: DEFAULTS.costCapKrw,
    // ★ 원가만 판정한다 — 기획서 v0.8 에 세는 법이 함께 있다.
    rated: true,
  },
] as const;

/**
 * 추세 패널은 **다섯 줄**이다 — 축 넷 + 「의도 미해석」.
 *
 * ⚠️ 순서가 원본과 같아야 한다(`home_page_widget.dart`). 「의도 미해석」은
 * KPI 카드에는 없고 추세에만 있다 — `lensUnreadRate` 가 그 축이다.
 * ★ `reject_unclear` 와 `aside` 를 **합쳐서** 센다(따로 그리면 층이 갈릴 때마다
 *   개선처럼 보인다).
 */
const TREND_ROWS: Array<{ label: string; metric: string; krw: boolean }> = [
  { label: '완주율', metric: 'completion', krw: false },
  { label: '진행 불가', metric: 'blocked', krw: false },
  { label: '의도 미해석', metric: 'unread', krw: false },
  { label: 'AI 오서술', metric: 'narration', krw: false },
  { label: '회차당 원가', metric: 'cost', krw: true },
];

export function HomePage() {
  // ★ 기간은 **화면 사이에 공유**된다 — 원본의 FFAppState().dateRangeKey 짝.
  const { rangeKey, setRangeKey } = useAppState();
  // ★ 임계값은 설정 화면이 바꾼다 — 바뀌면 이 화면이 바로 다시 그린다.
  const P = useSettings().settings;
  const { rows, loading, error, mode } = useLensData(rangeKey);

  if (loading) return <Frame rangeKey={rangeKey} onRange={setRangeKey}>불러오는 중…</Frame>;
  if (error !== null) {
    return (
      <Frame rangeKey={rangeKey} onRange={setRangeKey}>
        <Panel tone="error">데이터를 읽지 못했습니다 — {error}</Panel>
      </Frame>
    );
  }

  const values: Record<string, number> = {
    completion: lensCompletionRate(rows),
    blocked: lensBlockedRate(rows),
    narration: lensNarrationRate(rows),
    cost: lensCostPerRun(rows, P.costPerTurnKrw, P.usdKrwRate, P.costFeeRate),
    unread: lensUnreadRate(rows),
  };
  const fmt = (metric: string, v: number) =>
    metric === 'cost' ? lensFormatKrw(v) : lensFormatPct(v);
  const trendOf = (metric: string) =>
    lensDailyTrend(rows, metric, P.costPerTurnKrw, P.usdKrwRate, P.costFeeRate);

  // ⚠️ 빈 문자열이면 **위반이 없다는 뜻**이다 — 화면이 다른 문장으로 바꾼다.
  const alert = lensThresholdAlertV2(
    rows,
    values['completion'], P.targetCompletionRate,
    values['blocked'], P.maxBlockedRate,
    values['narration'], P.maxNarrationFailRate,
    values['cost'], P.costWarnKrw,
  );

  const diagnosis = lensTodayDiagnosis(
    rows,
    values['completion'], P.targetCompletionRate,
    values['blocked'], P.maxBlockedRate,
    values['narration'], P.maxNarrationFailRate,
    values['cost'], P.costWarnKrw,
  );

  // ⚠️⚠️ **배지는 집계가 보는 목록이 확정된 뒤에 만든다.** 앞에서 만들면
  //    「21회차」인데 아래 지표는 1회차를 써서 「전부 완주」로 읽힌다(실측).
  const badge = lensSourceBadge(rows, mode, RUN_WINDOW);

  const recent = lensRecentRuns(rows, 5);

  return (
    <Frame rangeKey={rangeKey} onRange={setRangeKey} badge={badge}>
      <div className="space-y-xl">
        <ThresholdBanner text={alert} />

        {/* ★★ **한 문장짜리 머리기사**로 세운다(2026-10-03 개편).
            ⚠️ 이 화면에서 가장 먼저 읽혀야 하는 줄인데 본문 크기로 깔려 있어
            KPI 카드에 묻혔다(사용자 지적 — 「디자인이 심심하다」). */}
        <section
          className="animate-rise relative overflow-hidden rounded-lg border border-border bg-card p-xl"
          style={{
            background:
              'radial-gradient(110% 120% at 0% 0%, color-mix(in srgb, var(--lens-primary) 16%, transparent), transparent 62%), var(--lens-surface)',
            boxShadow: '0 0 0 1px color-mix(in srgb, var(--lens-primary) 28%, transparent)',
          }}
          data-panel="오늘의 진단"
        >
          <span
            className="animate-sweep absolute inset-x-0 top-0 h-0.5"
            style={{ background: 'linear-gradient(90deg, var(--lens-primary), transparent 80%)' }}
            aria-hidden="true"
          />
          <h2 className="flex items-center gap-sm text-label-md tracking-wider text-muted-foreground">
            <Radar size={16} strokeWidth={2} aria-hidden="true" className="text-primary" />
            오늘의 진단
          </h2>
          <p className="mt-sm text-headline-sm leading-snug">{diagnosis}</p>
        </section>

        <div className="grid gap-lg [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
          {AXES.map((a, i) => {
            const v = values[a.metric]!;
            return (
              <KpiStatCard
                key={a.metric}
                accent={AXIS_COLOR[a.metric]!}
                icon={AXIS_ICON[a.metric]}
                delay={i}
                label={a.label}
                value={fmt(a.metric, v)}
                targetText={a.targetText}
                // ⚠️ 원본 카드에는 추세 문구가 없다 — 추세는 아래 패널이 말한다.
                //    이식 1판에 **없던 것을 더하지 않는다**(계획서 1-1절).
                status={a.rated ? lensStatusFor(v, a.warn, a.bad, a.higherIsBetter) : 'unrated'}
              />
            );
          })}
        </div>

        <Panel
          icon={TrendingUp}
          delay={2}
          title="7일 추세"
          note={
            '하루 단위로 집계했습니다. 선의 오른쪽 끝이 가장 최근 날이고, ' +
            '표본이 너무 적은 날은 점을 찍지 않습니다. 맨 오른쪽은 앞 절반 평균 ' +
            '대비 뒤 절반 평균의 변화입니다 — 기준선을 크게 웃도는 축은 절대값보다 ' +
            '이쪽이 신호입니다.'
          }
        >
          {/* ★★ **줄에서 카드로**(2026-10-03 개편). 전에는 다섯 줄이 각각
              1300px 를 가로질러 **선이 평평하게 눌려** 추세가 안 보였다
              (사용자 지적 — 「가로로 넓어 한눈에 안 들어온다」).
              ★ 카드로 쪼개면 폭이 1/3 이 되어 같은 변화가 **세 배 급해 보인다.** */}
          <div className="grid gap-lg [grid-template-columns:repeat(auto-fit,minmax(200px,1fr))]">
            {TREND_ROWS.map((t) => {
              const axis = AXES.find((a) => a.metric === t.metric);
              const v = values[t.metric]!;
              const series = trendOf(t.metric);
              // ★ 선 색은 KPI 카드와 **같은 판정 함수**를 쓴다 — 어긋날 수 없다.
              const tone =
                axis !== undefined && axis.rated
                  ? lensStatusFor(v, axis.warn, axis.bad, axis.higherIsBetter)
                  : 'neutral';
              return (
                <div
                  key={t.metric}
                  className="min-w-0 rounded-md border border-border bg-[var(--lens-bg)] p-lg"
                >
                  <div className="flex items-baseline justify-between gap-sm">
                    <span className="truncate text-title-sm text-muted-foreground">{t.label}</span>
                    <span
                      className="shrink-0 text-title-lg tabular-nums"
                      style={{ color: AXIS_COLOR[t.metric]! }}
                    >
                      {fmt(t.metric, v)}
                    </span>
                  </div>
                  <Sparkline
                    series={series}
                    tone={tone}
                    color={AXIS_COLOR[t.metric]!}
                    format={(x) => fmt(t.metric, x)}
                  />
                  <div className="text-label-md text-muted-foreground">
                    {lensTrendDelta(series, t.krw, axis?.higherIsBetter ?? false)}
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>

        <Panel title="최근 회차" icon={ListOrdered} accent="var(--lens-axis-runs)" delay={3}>
          {recent.length === 0 ? (
            // ★ 문구를 여기서 짓지 않는다 — 두 곳이 각자 말하면 언젠가 어긋난다.
            <p className="text-body-md text-muted-foreground">{lensRecentRunsNote(rows)}</p>
          ) : (
            <table className="w-full max-w-[52rem] text-body-md">
              <colgroup>
                {/* ⚠️ 폭을 안 주면 네 칸이 1300px 를 **균등 분할**해 가운데가
                    통째로 빈다(실측). ★ 값은 왼쪽에 모으고 꼬리만 오른쪽으로. */}
                <col className="w-28" />
                <col />
                <col className="w-24" />
                <col className="w-20" />
              </colgroup>
              <tbody>
                {recent.map((r) => (
                  <tr key={r.label} className="border-b border-border last:border-0">
                    <td className="py-sm font-mono text-label-md">{r.label}</td>
                    <td className="py-sm">{r.valueText}</td>
                    <td className="py-sm text-right text-muted-foreground">{r.pctText}</td>
                    <td className="w-16 py-sm text-right">
                      <span
                        className="rounded-pill px-sm py-xs text-label-sm"
                        style={{
                          background:
                            r.tone === 'good'
                              ? 'var(--lens-accent-success)'
                              : 'var(--lens-accent-warning)',
                          color: r.tone === 'good' ? 'var(--lens-success)' : 'var(--lens-warning)',
                        }}
                      >
                        {r.tone === 'good' ? '완주' : '진행'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      </div>
    </Frame>
  );
}

/**
 * 임계 배너.
 *
 * ⚠️⚠️ **빈 문자열은 「없음」이지 「0」이 아니다.** 비어 있으면 위반이 없다는
 * 뜻이고, 그때 화면은 **다른 문장**을 쓴다 — 셋을 판정하지 않는다는 사실까지
 * 함께 말해야 「4축 모두 안전」으로 읽히지 않는다.
 */
function ThresholdBanner({ text }: { text: string }) {
  const ok = text === '';
  return (
    <div
      className="rounded-md px-lg py-md text-body-md"
      style={{
        background: ok ? 'var(--lens-accent-success)' : 'var(--lens-accent-error)',
        color: ok ? 'var(--lens-success)' : 'var(--lens-error)',
      }}
    >
      {ok
        ? '회차당 원가는 경고선 안입니다. 나머지 셋은 기준선의 세는 법이 정해지지 않아 판정하지 않습니다.'
        : text}
    </div>
  );
}

function Frame({
  rangeKey,
  onRange,
  badge,
  children,
}: {
  rangeKey: string;
  onRange: (k: string) => void;
  badge?: string;
  children: React.ReactNode;
}) {
  return (
    <AppShell
      title="개요"
      subtitle="완주율 · 진행 불가 · AI 오서술 · 회차당 원가 4축 요약."
      accent="var(--lens-primary)"
      rangeKey={rangeKey}
      onRangeChange={onRange}
    >
      {badge !== undefined ? (
        <div className="mb-lg inline-block rounded-pill bg-[var(--lens-accent-primary)] px-lg py-xs text-label-md text-primary">
          {badge}
        </div>
      ) : null}
      {children}
    </AppShell>
  );
}
